import { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Treemap, ResponsiveContainer, Tooltip as ReTooltip } from 'recharts';
import {
  MARKET_FIT_DISTRIBUTION_BANDS,
  getMarketFitDistributionStyleForPercent,
  getContrastingTextOnHex,
} from '@/lib/marketFitDistributionScale';

/** Slightly darker than tailwind gray-400 so white label stays readable on unknown cells. */
const UNKNOWN_FILL = '#6B7280';

function isUnknownCountryLabel(name) {
  return String(name || '').trim().toLowerCase() === '(unknown)';
}

function truncateSvgText(s, maxLen) {
  const t = String(s || '');
  if (t.length <= maxLen) return t;
  return `${t.slice(0, Math.max(0, maxLen - 1))}…`;
}

/** ~0.52em per character at 600 weight — conservative for SVG text width. */
function maxCharsForWidth(innerWidth, fontSize) {
  return Math.max(0, Math.floor(innerWidth / (fontSize * 0.52)));
}

function bundleSmallCountries(cells, minAccounts = 2) {
  const list = Array.isArray(cells) ? cells : [];
  const kept = [];
  const bundled = [];
  for (const c of list) {
    const ac = Number(c.account_count) || 0;
    if (ac < minAccounts && !isUnknownCountryLabel(c.country)) bundled.push(c);
    else kept.push(c);
  }
  if (bundled.length <= 1) return { rows: list, otherCount: 0 };

  const sum = (arr, key) => arr.reduce((s, c) => s + (Number(c[key]) || 0), 0);
  const totalSends = sum(bundled, 'total_sends');
  const weightedRate = (key) => {
    if (totalSends <= 0) {
      const vals = bundled.map((c) => Number(c[key])).filter(Number.isFinite);
      return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : NaN;
    }
    return bundled.reduce((s, c) => s + (Number(c[key]) || 0) * (Number(c.total_sends) || 0), 0) / totalSends;
  };

  kept.push({
    country: `Other markets (${bundled.length})`,
    account_count: sum(bundled, 'account_count'),
    total_sends: totalSends,
    total_human_opens: sum(bundled, 'total_human_opens'),
    total_human_clicks: sum(bundled, 'total_human_clicks'),
    avg_open_rate: weightedRate('avg_open_rate'),
    avg_click_rate: weightedRate('avg_click_rate'),
  });
  return { rows: kept, otherCount: bundled.length };
}

function buildTreemapLabelLayout({ name, w, h, accountCount, metricVal, hasMetric }) {
  const pad = 6;
  const innerW = Math.max(0, w - pad * 2);

  // Below this — rely on hover tooltip only (avoids overlapping "Uga"/"Lat" slivers).
  if (w < 52 || h < 36 || innerW < 28) return null;

  const tier = w >= 100 && h >= 76 ? 'full' : w >= 68 && h >= 52 ? 'compact' : 'minimal';
  const fontSize = tier === 'full' ? 12 : tier === 'compact' ? 11 : 10;
  const lineHeight = fontSize + 3;
  const maxChars = maxCharsForWidth(innerW, fontSize);
  if (maxChars < 3) return null;

  const lines = [];
  const countryLine = truncateSvgText(name, Math.max(3, maxChars));
  if (!countryLine || countryLine.length < 2) return null;

  if (tier === 'full') {
    lines.push(countryLine);
    if (h >= 58) lines.push(`${accountCount} accounts`);
    if (h >= 72 && hasMetric) lines.push(`${metricVal.toFixed(1)}%`);
  } else if (tier === 'compact') {
    lines.push(countryLine);
    if (h >= 58 && hasMetric) lines.push(`${metricVal.toFixed(1)}%`);
  } else {
    lines.push(countryLine);
  }

  const totalTextH = (lines.length - 1) * lineHeight + fontSize;
  const centered = tier === 'minimal' || (tier === 'compact' && lines.length === 1);
  return {
    lines,
    fontSize,
    lineHeight,
    pad,
    centered,
    textX: centered ? w / 2 : pad,
    textAnchor: centered ? 'middle' : 'start',
    startY: centered ? (h - totalTextH) / 2 + fontSize : fontSize + 4,
  };
}

function TreemapCell({
  metric,
  x,
  y,
  width,
  height,
  name,
  children,
  account_count,
  avg_open_rate,
  avg_click_rate,
  colorWeightOpen,
  colorWeightClick,
}) {
  const hasNested = children && Array.isArray(children) && children.length > 0;
  if (hasNested) return null;

  const unknown = isUnknownCountryLabel(name);
  const metricVal = metric === 'open' ? Number(avg_open_rate) : Number(avg_click_rate);
  const hasMetric = Number.isFinite(metricVal);

  const palettePct = metric === 'open' ? Number(colorWeightOpen) : Number(colorWeightClick);
  const hasPalette = Number.isFinite(palettePct);

  const fill = unknown
    ? UNKNOWN_FILL
    : hasPalette
      ? getMarketFitDistributionStyleForPercent(palettePct).backgroundColor
      : '#E5E7EB';
  const textColor = getContrastingTextOnHex(fill, { threshold: 0.35 });

  const ac = Number(account_count) || 0;
  const w = Number(width) || 0;
  const h = Number(height) || 0;
  if (w < 2 || h < 2) return <g />;

  const layout = buildTreemapLabelLayout({
    name,
    w,
    h,
    accountCount: ac,
    metricVal,
    hasMetric,
  });

  const fontSize = layout?.fontSize ?? 12;
  const lineHeight = layout?.lineHeight ?? fontSize + 4;
  const isLightFill = textColor === '#0A1A2F';
  const clipId = `treemap-clip-${Math.round(x)}-${Math.round(y)}-${Math.round(w)}-${Math.round(h)}`;

  return (
    <g>
      <defs>
        <clipPath id={clipId}>
          <rect x={x} y={y} width={w} height={h} />
        </clipPath>
      </defs>
      <rect x={x} y={y} width={w} height={h} fill={fill} stroke="rgba(255,255,255,0.75)" strokeWidth={1.5} />
      {layout && (
        <g clipPath={`url(#${clipId})`}>
          {layout.lines.map((line, i) => (
            <text
              key={`${name}-${i}`}
              x={x + layout.textX}
              y={y + layout.startY + i * lineHeight}
              fill={textColor}
              fontSize={fontSize}
              fontWeight={i === 0 ? 700 : 500}
              letterSpacing={i === 0 ? '0.01em' : '0'}
              textAnchor={layout.textAnchor}
              stroke={isLightFill ? 'rgba(255,255,255,0.55)' : 'rgba(10,26,47,0.35)'}
              strokeWidth={isLightFill ? 2.5 : 0.6}
              paintOrder="stroke fill"
              style={{ pointerEvents: 'none' }}
            >
              {line}
            </text>
          ))}
        </g>
      )}
    </g>
  );
}

function HeatmapTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d || d.name == null || String(d.name).trim() === '') return null;
  const country = d.name;
  const unknown = isUnknownCountryLabel(country);
  const ao = Number(d.avg_open_rate);
  const avgClick = Number(d.avg_click_rate);
  const sends = Number(d.total_sends) || 0;
  const accounts = Number(d.account_count) || 0;
  const opens = Number(d.total_human_opens) || 0;
  const clicks = Number(d.total_human_clicks) || 0;

  return (
    <div className="bg-white border border-gray-200 rounded shadow-md px-3 py-2 text-xs max-w-[260px]">
      <p className="font-semibold text-[#0A1A2F] mb-1">{country}</p>
      <p className="text-gray-600 mb-1">
        {accounts.toLocaleString()} accounts · {sends.toLocaleString()} sends
      </p>
      <p className="text-gray-600">Avg true open rate: {Number.isFinite(ao) ? `${ao.toFixed(1)}%` : '—'}</p>
      <p className="text-gray-600">Avg true click rate: {Number.isFinite(avgClick) ? `${avgClick.toFixed(1)}%` : '—'}</p>
      <p className="text-gray-600 mt-1">Total true opens: {opens.toLocaleString()}</p>
      <p className="text-gray-600">Total true clicks: {clicks.toLocaleString()}</p>
      {unknown && (
        <p className="text-gray-500 mt-2 border-t border-gray-100 pt-2">
          These accounts have no country populated on the account record.
        </p>
      )}
    </div>
  );
}

function ColorScaleLegend({ metric, clickColorCap, openColorLo, openColorHi }) {
  const ordered = [...MARKET_FIT_DISTRIBUTION_BANDS].reverse();
  return (
    <div className="mt-3 space-y-1">
      <div className="flex items-center gap-2 text-[11px] text-gray-500">
        <span className="tabular-nums shrink-0">0%</span>
        <div className="flex flex-1 min-w-0 h-3 rounded overflow-hidden border border-gray-200">
          {ordered.map((b) => (
            <div key={b.range} className="flex-1 min-w-0 h-full" style={{ backgroundColor: b.color }} title={b.range} />
          ))}
        </div>
        <span className="tabular-nums shrink-0">100%</span>
      </div>
      <p className="text-[10px] text-gray-500 leading-snug">
        {metric === 'open' ? (
          Number.isFinite(openColorLo) && Number.isFinite(openColorHi) ? (
            <>
              Color = relative open strength in this view (0% = lowest avg open ≈{' '}
              <span className="tabular-nums">{openColorLo.toFixed(1)}%</span>, 100% = highest ≈{' '}
              <span className="tabular-nums">{openColorHi.toFixed(1)}%</span>). Same Market fit palette; labels show each
              country’s actual %.
            </>
          ) : (
            <>Color uses the Market fit palette from avg true open % when data is available.</>
          )
        ) : (
          <>
            Color = relative click strength in this view (100% = highest country, capped at{' '}
            <span className="tabular-nums">{Number(clickColorCap || 0).toFixed(1)}%</span> avg click). Labels still show
            each country’s actual avg click %.
          </>
        )}
      </p>
    </div>
  );
}

/**
 * @param {{ cells: Array<Record<string, unknown>>, loadError: boolean }} props
 */
export default function CountryActivityHeatmapCard({ cells, loadError }) {
  const [metric, setMetric] = useState('open');

  const { data, clickColorCap, openColorLo, openColorHi, otherCountryCount } = useMemo(() => {
    const { rows: list, otherCount } = bundleSmallCountries(Array.isArray(cells) ? cells : []);
    const clickRates = list
      .map((c) => Number(c.avg_click_rate))
      .filter((n) => Number.isFinite(n) && n >= 0);
    const maxClick = clickRates.length ? Math.max(...clickRates) : 0;
    const clickCap =
      maxClick > 0 ? Math.min(50, Math.max(2, maxClick * 1.15)) : 12;

    const openRates = list
      .map((c) => Number(c.avg_open_rate))
      .filter((n) => Number.isFinite(n) && n >= 0);
    const minOpen = openRates.length ? Math.min(...openRates) : NaN;
    const maxOpen = openRates.length ? Math.max(...openRates) : NaN;
    const OPEN_SYNTHETIC_SPAN_PP = 6;
    let openLo = minOpen;
    let openHi = maxOpen;
    if (Number.isFinite(minOpen) && Number.isFinite(maxOpen)) {
      const span = maxOpen - minOpen;
      if (span < 0.05) {
        const mid = (minOpen + maxOpen) / 2;
        openLo = mid - OPEN_SYNTHETIC_SPAN_PP / 2;
        openHi = mid + OPEN_SYNTHETIC_SPAN_PP / 2;
      }
    }

    const rows = list.map((c) => {
      const country = String(c.country ?? '(unknown)');
      const ac = Number(c.account_count) || 0;
      const ar = Number(c.avg_open_rate);
      const cr = Number(c.avg_click_rate);
      let colorWeightOpen = NaN;
      if (Number.isFinite(ar) && Number.isFinite(openLo) && Number.isFinite(openHi) && openHi > openLo) {
        colorWeightOpen = Math.min(100, Math.max(0, ((ar - openLo) / (openHi - openLo)) * 100));
      } else if (Number.isFinite(ar)) {
        colorWeightOpen = 50;
      }
      let colorWeightClick = NaN;
      if (Number.isFinite(cr) && clickCap > 0) {
        colorWeightClick = Math.min(100, Math.max(0, (cr / clickCap) * 100));
      }
      return {
        name: country,
        value: Math.max(1, ac),
        account_count: ac,
        total_sends: Number(c.total_sends) || 0,
        total_human_opens: Number(c.total_human_opens) || 0,
        total_human_clicks: Number(c.total_human_clicks) || 0,
        avg_open_rate: ar,
        avg_click_rate: cr,
        colorWeightOpen,
        colorWeightClick,
      };
    });
    return {
      data: rows,
      clickColorCap: clickCap,
      openColorLo: openLo,
      openColorHi: openHi,
      otherCountryCount: otherCount,
    };
  }, [cells]);

  if (!loadError && data.length === 0) return null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle className="text-lg">Country activity</CardTitle>
          <div
            className="inline-flex rounded-lg border border-gray-200 p-0.5 bg-gray-50 self-start"
            role="group"
            aria-label="Color metric"
          >
            <button
              type="button"
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                metric === 'open'
                  ? 'bg-white text-[#0A1A2F] shadow-sm'
                  : 'text-gray-600 hover:text-[#0A1A2F]'
              }`}
              onClick={() => setMetric('open')}
            >
              True open rate
            </button>
            <button
              type="button"
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                metric === 'click'
                  ? 'bg-white text-[#0A1A2F] shadow-sm'
                  : 'text-gray-600 hover:text-[#0A1A2F]'
              }`}
              onClick={() => setMetric('click')}
            >
              True click rate
            </button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loadError && (
          <p className="text-sm text-gray-600 py-6">
            Could not load country activity. Check your connection and try again.
          </p>
        )}
        {!loadError && data.length > 0 && (
          <>
            <div className="h-[360px] w-full min-h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <Treemap
                  key={metric}
                  data={data}
                  dataKey="value"
                  nameKey="name"
                  aspectRatio={4 / 3}
                  stroke="#fff"
                  isAnimationActive={false}
                  content={<TreemapCell metric={metric} />}
                >
                  <ReTooltip content={<HeatmapTooltip />} />
                </Treemap>
              </ResponsiveContainer>
            </div>
            <ColorScaleLegend
              metric={metric}
              clickColorCap={clickColorCap}
              openColorLo={openColorLo}
              openColorHi={openColorHi}
            />
            {otherCountryCount > 0 && (
              <p className="text-[10px] text-gray-500 mt-1 leading-snug">
                {otherCountryCount} countries with fewer than 2 accounts are grouped as &ldquo;Other markets&rdquo;.
                Hover any tile for full country details; small tiles hide labels when there isn&apos;t room to read them.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
